import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useSpeechRecognition } from '@/hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '@/hooks/useSpeechSynthesis';
import { useCreateConversation, useSendChatMessage } from '@/hooks/mutations/useAiMutations';

import { type VoiceContext, getContextConfig, buildContextualMessage } from './voiceContextPrompts';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type VoicePhase = 'idle' | 'listening' | 'processing' | 'responding';

interface UseVoiceAssistantOptions {
  context: VoiceContext;
  open: boolean;
  onClose: () => void;
}

interface UseVoiceAssistantReturn {
  phase: VoicePhase;
  transcript: string;
  responseText: string;
  /** Error message to display in the overlay. Null when no error. */
  errorMessage: string | null;
  isSpeaking: boolean;
  isSupported: boolean;
  startListening: () => void;
  stopListening: () => void;
  stopSpeaking: () => void;
  /** Speak the context greeting. Call from a user gesture context (event handler). */
  greet: () => void;
  /** Retry the last failed request. */
  retry: () => void;
  reset: () => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Extract the first 2 sentences from a text string, capped at 200 characters.
 * Used to trim AI responses to a TTS-friendly length.
 */
function extractTtsSummary(text: string): string {
  const sentences = text.split(/(?<=[.!?])\s+/);
  const summary = sentences.slice(0, 2).join(' ');
  return summary.length > 200 ? summary.slice(0, 200) : summary;
}

/** STT safety timeout — if no final result after this, go back to idle. */
const STT_SAFETY_TIMEOUT_MS = 15_000;

/** Delay after TTS ends before restarting STT (avoids mic echo). */
const POST_TTS_DELAY_MS = 300;

/** Inactivity timeout — auto-close overlay if idle for this long after a response. */
const INACTIVITY_TIMEOUT_MS = 20_000;

/** Tag the AI appends when a task is complete. */
const SESSION_COMPLETE_TAG = '[SESSION_COMPLETE]';

/** Strip the session complete tag from visible text. */
function stripSessionTag(text: string): string {
  return text.replace(/\[SESSION_COMPLETE\]/gi, '').trim();
}

/** Check if the AI response signals session completion. */
function hasSessionCompleteTag(text: string): boolean {
  return text.includes(SESSION_COMPLETE_TAG);
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function useVoiceAssistant({
  context,
  open,
  onClose,
}: UseVoiceAssistantOptions): UseVoiceAssistantReturn {
  const { i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');

  // ── Core state ──────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<VoicePhase>('idle');
  const [responseText, setResponseText] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Pending transcript captured when final STT result arrives
  const pendingTranscriptRef = useRef<string>('');

  // Ref mirror of phase to avoid stale closures in callbacks/effects
  const phaseRef = useRef<VoicePhase>('idle');
  const setPhaseSync = useCallback((next: VoicePhase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Guard against double-firing the processing effect (React StrictMode etc.)
  const processingFiredRef = useRef(false);

  // Track whether TTS was ever speaking during the 'responding' phase
  const wasSpeakingRef = useRef(false);

  // Track when listening started — used to detect phantom simulator results
  const listeningStartedAtRef = useRef<number>(0);

  // Safety timeout ref for STT sessions that hang
  const sttTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inactivity timeout — auto-close after idle period following a response
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Whether the AI signaled session completion
  const sessionCompleteRef = useRef(false);

  // Stable ref for onClose to avoid re-render loops
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // ── Dependent mutation hooks ─────────────────────────────────────────────────
  const createConversation = useCreateConversation();
  // useSendChatMessage requires a string at call time; use '__pending__' until we have a real id
  const sendMessage = useSendChatMessage(conversationId ?? '__pending__');

  // ── Speech hooks ─────────────────────────────────────────────────────────────
  const {
    isSpeaking,
    isSupported: isTtsSupported,
    speak,
    stop: stopTts,
    prime: primeTts,
  } = useSpeechSynthesis();

  const handleSttError = useCallback(
    (error: string) => {
      const messages: Record<string, string> = {
        'not-allowed': isFr
          ? "L'accès au microphone a été refusé."
          : 'Microphone access was denied.',
        'audio-capture': isFr ? 'Aucun microphone détecté.' : 'No microphone detected.',
        network: isFr
          ? 'Une connexion réseau est requise pour la reconnaissance vocale.'
          : 'A network connection is required for speech recognition.',
      };
      const msg =
        messages[error] ??
        (isFr ? 'Erreur de reconnaissance vocale.' : 'Speech recognition error.');
      toast.error(msg);
      setPhaseSync('idle');
    },
    [isFr, setPhaseSync],
  );

  const handleSttResult = useCallback(
    (text: string, isFinal: boolean) => {
      if (!isFinal || !text.trim()) return;

      // Phantom results from simulators arrive instantly with very short text.
      // Real speech always takes > 300ms even for a single word.
      const elapsed = Date.now() - listeningStartedAtRef.current;
      if (elapsed < 300 && text.trim().length <= 4) return;

      // Clear safety timeout — we got a valid result
      if (sttTimeoutRef.current) {
        clearTimeout(sttTimeoutRef.current);
        sttTimeoutRef.current = null;
      }

      pendingTranscriptRef.current = text.trim();
      setPhaseSync('processing');
    },
    [setPhaseSync],
  );

  const {
    isListening,
    isSupported: isSttSupported,
    transcript,
    startListening: sttStart,
    stopListening: sttStop,
    resetTranscript,
  } = useSpeechRecognition({
    continuous: false,
    interimResults: true,
    onResult: handleSttResult,
    onError: handleSttError,
  });

  const isSupported = isSttSupported && isTtsSupported;

  // ── Helper: start STT with safety timeout ─────────────────────────────────
  const startSttWithTimeout = useCallback(() => {
    listeningStartedAtRef.current = Date.now();
    sttStart();

    // Safety timeout: if STT hangs without producing a result, go back to idle.
    if (sttTimeoutRef.current) clearTimeout(sttTimeoutRef.current);
    sttTimeoutRef.current = setTimeout(() => {
      if (phaseRef.current === 'listening') {
        sttStop();
        setPhaseSync('idle');
      }
    }, STT_SAFETY_TIMEOUT_MS);
  }, [sttStart, sttStop, setPhaseSync]);

  // ── Internal reset helper ────────────────────────────────────────────────────
  const resetState = useCallback(
    (keepConversation = false) => {
      sttStop();
      stopTts();
      resetTranscript();
      pendingTranscriptRef.current = '';
      processingFiredRef.current = false;
      wasSpeakingRef.current = false;
      if (sttTimeoutRef.current) {
        clearTimeout(sttTimeoutRef.current);
        sttTimeoutRef.current = null;
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      sessionCompleteRef.current = false;
      setPhaseSync('idle');
      setResponseText('');
      setErrorMessage(null);
      if (!keepConversation) {
        setConversationId(null);
      }
    },
    [sttStop, stopTts, resetTranscript, setPhaseSync],
  );

  // ── Public API ───────────────────────────────────────────────────────────────
  const startListening = useCallback(() => {
    if (!isSupported) return;
    primeTts(); // Unlock Chrome TTS within user gesture context
    setErrorMessage(null);
    resetTranscript();
    pendingTranscriptRef.current = '';
    setPhaseSync('listening');
    startSttWithTimeout();
  }, [isSupported, primeTts, resetTranscript, setPhaseSync, startSttWithTimeout]);

  const stopListening = useCallback(() => {
    sttStop();
    if (sttTimeoutRef.current) {
      clearTimeout(sttTimeoutRef.current);
      sttTimeoutRef.current = null;
    }
    setPhaseSync('idle');
  }, [sttStop, setPhaseSync]);

  const stopSpeaking = useCallback(() => {
    stopTts();
    // Ending speech manually → back to idle
    setPhaseSync('idle');
  }, [stopTts, setPhaseSync]);

  const reset = useCallback(() => {
    resetState(false);
  }, [resetState]);

  // ── Greeting is triggered by the overlay component via greet(), not an effect,
  //    because Web Speech Synthesis requires a recent user gesture context. ─────

  // ── Effect: cleanup when overlay closes ─────────────────────────────────────
  useEffect(() => {
    if (open) return;
    resetState(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Effect: process transcript when phase becomes 'processing' ──────────────
  useEffect(() => {
    if (phase !== 'processing') {
      // Reset the guard whenever we leave processing
      processingFiredRef.current = false;
      return;
    }

    // Guard against double-fire
    if (processingFiredRef.current) return;
    processingFiredRef.current = true;

    const capturedTranscript = pendingTranscriptRef.current;
    if (!capturedTranscript) {
      setPhaseSync('idle');
      return;
    }

    const message = buildContextualMessage(context, capturedTranscript);

    const run = async () => {
      try {
        let assistantContent: string | null = null;

        if (conversationId === null) {
          const result = await createConversation.mutateAsync(message);
          setConversationId(result.conversation.id);
          assistantContent = result.assistant?.content ?? null;
        } else {
          const result = await sendMessage.mutateAsync(message);
          assistantContent = result.assistant.content;
        }

        if (assistantContent) {
          const isComplete = hasSessionCompleteTag(assistantContent);
          const cleanContent = stripSessionTag(assistantContent);
          sessionCompleteRef.current = isComplete;

          setResponseText(cleanContent);
          wasSpeakingRef.current = false;
          setPhaseSync('responding');
          speak(extractTtsSummary(cleanContent));
        } else {
          setPhaseSync('idle');
        }
      } catch (err) {
        // ApiError from our backend has a .code property with specific error codes.
        const apiErr = err as { code?: string; message?: string };
        const code = apiErr?.code ?? '';

        let msg: string;
        if (code === 'AI_RATE_LIMITED' || code === 'AI_OVERLOADED') {
          msg = isFr
            ? "L'assistant est temporairement surcharge. Patientez quelques secondes puis appuyez sur Reessayer."
            : 'The assistant is temporarily overloaded. Wait a few seconds and tap Retry.';
        } else if (code === 'INSUFFICIENT_CREDITS') {
          msg = isFr
            ? 'Credits IA insuffisants. Achetez des credits pour continuer.'
            : 'Insufficient AI credits. Purchase credits to continue.';
        } else {
          msg = isFr
            ? "Une erreur s'est produite. Verifiez votre connexion et reessayez."
            : 'An error occurred. Check your connection and try again.';
        }
        setErrorMessage(msg);
        setPhaseSync('idle');
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ── Effect: detect when TTS finishes during 'responding' phase ───────────────
  // After TTS finishes:
  //  - If session complete → auto-close overlay after a short pause
  //  - Otherwise → restart listening for next utterance
  useEffect(() => {
    if (phase !== 'responding') return;

    if (isSpeaking) {
      wasSpeakingRef.current = true;
    } else if (wasSpeakingRef.current) {
      // TTS was speaking and has now stopped
      wasSpeakingRef.current = false;

      if (sessionCompleteRef.current) {
        // AI signaled task is done — auto-close after a brief pause
        sessionCompleteRef.current = false;
        const timer = setTimeout(() => {
          onCloseRef.current();
        }, 1500);
        return () => clearTimeout(timer);
      }

      // Small delay to avoid mic picking up residual TTS audio, then restart listening
      const timer = setTimeout(() => {
        if (phaseRef.current !== 'responding') return;
        pendingTranscriptRef.current = '';
        resetTranscript();
        setPhaseSync('listening');
        startSttWithTimeout();
      }, POST_TTS_DELAY_MS);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpeaking, phase, setPhaseSync]);

  // ── Effect: inactivity auto-close ──────────────────────────────────────────
  // If the overlay is idle (not listening, not processing, not responding) for
  // INACTIVITY_TIMEOUT_MS after having had at least one response, auto-close.
  useEffect(() => {
    // Clear any existing timer on phase change
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }

    // Only start inactivity timer when idle AND we've had a conversation
    if (phase === 'idle' && conversationId !== null) {
      inactivityTimeoutRef.current = setTimeout(() => {
        onCloseRef.current();
      }, INACTIVITY_TIMEOUT_MS);
    }

    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
    };
  }, [phase, conversationId]);

  // Suppress unused-var warning for isListening (used indirectly by STT internals)
  void isListening;

  /** Call from an event handler (user gesture context) to speak the greeting. */
  const greet = useCallback(() => {
    // Prime Chrome's speechSynthesis within user gesture context.
    // This unlocks TTS for subsequent async calls (API responses).
    primeTts();
    const config = getContextConfig(context);
    const greeting = isFr ? config.greeting.fr : config.greeting.en;
    speak(greeting);
  }, [context, isFr, speak, primeTts]);

  /** Retry: clear error and re-enter listening state. */
  const retry = useCallback(() => {
    setErrorMessage(null);
    setResponseText('');
    resetTranscript();
    pendingTranscriptRef.current = '';
    setPhaseSync('listening');
    startSttWithTimeout();
  }, [resetTranscript, setPhaseSync, startSttWithTimeout]);

  return {
    phase,
    transcript,
    responseText,
    errorMessage,
    isSpeaking,
    isSupported,
    startListening,
    stopListening,
    stopSpeaking,
    greet,
    retry,
    reset,
  };
}
